import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { IoVolumeMuteSharp, IoClose, IoEye, IoSend } from "react-icons/io5";
import { FaUserCircle, FaRegSmile, FaGift, FaDownload, FaUsers, FaLink } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Link } from "react-router-dom";
import { initFacebookPixel, trackEvent } from '../../pixel';
import { config } from '../../config';
import { motion, AnimatePresence } from 'framer-motion';
import { PixelContext } from '../Context/PixelContext';

const apiUrl = config.apiUrl;
const videoUrl = config.videoUrl;

const Live = () => {
  // State management
  const [time, setTime] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showOffer, setShowOffer] = useState(false);
  const [offerClosed, setOfferClosed] = useState(false);
  const [seenMessages, setSeenMessages] = useState([]);
  const [viewers, setViewers] = useState('');
  const [showEndMessage, setShowEndMessage] = useState(false);
  const [chatMessage, setChatMessage] = useState(['']);
  const [livePage, setLivePage] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showChatOptions, setShowChatOptions] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeBonusLinks, setActiveBonusLinks] = useState([]);
  const [showBonusLinkPopup, setShowBonusLinkPopup] = useState(null);
  const [dismissedBonusLinks, setDismissedBonusLinks] = useState({});

  const videoRef = useRef(null);
  const chatRef = useRef(null);
  const pixelInitialized = useRef(false);
  const hasRestoredTime = useRef(false);
  const { slug } = useParams();

  // Bonus link timers ref
  const bonusLinkTimers = useRef({});

  console.log("Live Page Data:", livePage);

  // Pixel Context
  const { pixel } = useContext(PixelContext);
  const pixelId = pixel?.pixel_id || null;

  const name = localStorage.getItem('name');
  const email = localStorage.getItem('email');
  const whatsapp = localStorage.getItem('whatsapp');

  // Initialize Facebook Pixel
  useEffect(() => {
    if (pixelId && !pixelInitialized.current) {
      initFacebookPixel(pixelId);
      pixelInitialized.current = true;
      
      // Standard events
      trackEvent('ViewContent', {
        content_type: 'live_stream',
        content_ids: [slug]
      });
      
      // Custom event for live stream viewing
      trackEvent('LiveStreamView', {
        stream_id: slug,
        duration: livePage?.duration
      });
    }
  }, [pixelId, livePage, slug]);

  const handlePlay = useCallback(() => {
    trackEvent('LiveStreamPlay', {
      stream_id: slug
    });
  }, [slug]);

  const handleOfferShow = () => {
    trackEvent('OfferPresented', {
      offer_id: slug,
      time_in_stream: time
    });
  };

  // Track bonus link click
  const handleBonusLinkClick = (bonusLink) => {
    trackEvent('BonusLinkClick', {
      link_type: bonusLink.type,
      link_title: bonusLink.title,
      time_in_stream: time
    });
  };

  // Check if user data exists
  useEffect(() => {
    if (!email || !whatsapp) {
      setShowEndMessage(true);
    }
  }, [email, whatsapp]);

  // Fetch live page data
  useEffect(() => {
    axios.get(`${apiUrl}/lives/${slug}`)
      .then((response) => {
        setLivePage(response.data.live);
        setViewers(response.data.live.seenPeople);
        setLoading(false);
      })
      .catch((error) => {
        setError("Data Fetching Problem!");
        setLoading(false);
      });
  }, [slug]);

  // Fetch chat messages
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${apiUrl}/chats/${slug}`);
        setChatMessage(response.data);
      } catch (error) {
        setError("Data Fetching Problem!", error);
      }
    };

    fetchData();
  }, [slug]);

  // Handle bonus links based on video time
  useEffect(() => {
    if (!livePage?.bonus_links) return;

    const checkBonusLinks = () => {
      const currentTime = Math.floor(time);
      const newActiveLinks = [];

      livePage.bonus_links.forEach((link) => {
        if (link.is_active && currentTime >= link.show_time && currentTime <= link.show_time + link.duration) {
          newActiveLinks.push(link);
        }
      });

      setActiveBonusLinks(newActiveLinks);
    };

    checkBonusLinks();
  }, [time, livePage?.bonus_links]);

  // Handle showing bonus links popup with timer
  useEffect(() => {
    if (!livePage?.bonus_links) return;

    const currentTime = Math.floor(time);
    
    livePage.bonus_links.forEach((link) => {
      if (link.is_active && 
          currentTime === link.show_time && 
          !bonusLinkTimers.current[link.id] &&
          !dismissedBonusLinks[link.id]) {
        
        setShowBonusLinkPopup(link);
        bonusLinkTimers.current[link.id] = true;
        
        trackEvent('BonusLinkShow', {
          link_type: link.type,
          link_title: link.title,
          time_in_stream: currentTime
        });

        // Auto hide after duration
        const timer = setTimeout(() => {
          setShowBonusLinkPopup(null);
        }, link.duration * 1000);

        return () => clearTimeout(timer);
      }
    });
  }, [time, livePage?.bonus_links, dismissedBonusLinks]);

  // Handle video time and messages
  useEffect(() => {
    const interval = setInterval(() => {
      if (videoRef.current) {
        const currentTime = videoRef.current.currentTime;
        setTime(currentTime);

        const newMessages = chatMessage.filter(
          (chat) =>
            currentTime >= chat.messageTime &&
            !seenMessages.includes(chat.messageTime)
        );

        if (newMessages.length > 0) {
          setMessages((prevMessages) => [...prevMessages, ...newMessages]);
          setSeenMessages((prevSeen) => [
            ...prevSeen,
            ...newMessages.map((msg) => msg.messageTime),
          ]);
        }

        if (currentTime >= livePage?.offerTime && !offerClosed) {
          setShowOffer(true);
          handleOfferShow();
        }
      }
    }, 200);

    return () => {
      clearInterval(interval);
    };
  }, [chatMessage, seenMessages, offerClosed, livePage]);

  // Simulate viewer count changes
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers((prevViewers) => {
        const change = Math.floor(Math.random() * 4) + 1;
        const increase = Math.random() < 0.5;
        return Math.max(0, prevViewers + (increase ? change : -change));
      });
    }, 1000 + Math.random() * 1000);

    return () => {
      clearInterval(viewerInterval);
    };
  }, []);

  // Video event handlers
  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      const savedTime = localStorage.getItem(`videoTime_${slug}`);
      if (savedTime && !hasRestoredTime.current) {
        hasRestoredTime.current = true;
        videoRef.current.currentTime = parseFloat(savedTime);
        setTime(parseFloat(savedTime));
      }
    }
  }, [slug]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      localStorage.setItem(`videoTime_${slug}`, currentTime.toString());
    }
  }, [slug]);

  const ensurePlay = useCallback(() => {
    if (videoRef.current) {
      videoRef.current
        .play()
        .then(() => {
          console.log("Video is playing successfully.");
        })
        .catch((err) => {
          console.error("Error during video play:", err);
        });
    }
  }, []);

  const handleVideoEnded = useCallback(() => {
    localStorage.removeItem(`videoTime_${slug}`);
    localStorage.removeItem(`name`);
    localStorage.removeItem(`email`);
    localStorage.removeItem(`whatsapp`);
  }, [slug]);

  // Chat functions
  const toggleMute = () => {
    if (isMuted) {
      setIsMuted(false);
      videoRef.current.muted = false;
    }
  };

  const handleSendMessage = useCallback(() => {
    if (newMessage.trim() !== "") {
      axios
        .post(`${apiUrl}/chats`, {
          slug: slug,
          name: name,
          email: email,
          whatsapp: whatsapp,
          message: newMessage,
          messageTime: time,
        })
        .then(() => {
          setMessages((prevMessages) => [
            ...prevMessages,
            { name: "You", message: newMessage, isYou: true },
          ]);
          setNewMessage("");
        })
        .catch((err) => console.error("Failed to send message:", err));
    }
  }, [newMessage, slug, name, email, whatsapp, time]);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleCloseOffer = () => {
    setShowOffer(false);
    setOfferClosed(true);
  };

  const handleCloseBonusLinkPopup = (linkId) => {
    setShowBonusLinkPopup(null);
    setDismissedBonusLinks(prev => ({ ...prev, [linkId]: true }));
  };

  // Get icon for bonus link type
  const getBonusLinkIcon = (type) => {
    switch (type) {
      case 'gift': return <FaGift className="text-yellow-500" />;
      case 'download': return <FaDownload className="text-blue-500" />;
      case 'secret_group': return <FaUsers className="text-green-500" />;
      default: return <FaLink className="text-gray-500" />;
    }
  };

  // Avatar rendering
  const renderAvatar = useMemo(
    () => (chat) => {
      if (chat.isYou) {
        return (
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
            You
          </div>
        );
      }
      if (chat.avatar) {
        return (
          <img
            src={chat.avatar}
            alt={`${chat.name} avatar`}
            className="w-8 h-8 rounded-full"
          />
        );
      } else {
        const initials = chat.name
          .split(" ")
          .map((word) => word[0])
          .join("")
          .toUpperCase();
        return (
          <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white font-bold">
            {initials}
          </div>
        );
      }
    },
    []
  );

  // Format time display
  const formatTime = (seconds) => {
    const date = new Date(seconds * 1000);
    return date.toISOString().substr(11, 8);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    );
  }

  if (showEndMessage) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-white p-4">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 text-center">
          Webinar Has Ended
        </h1>
        <p className="text-gray-600 text-lg mb-8 text-center max-w-md">
          Thank you for attending our webinar! The recording will be available soon.
        </p>
        <Link
          to="/"
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Return to Homepage
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Main Content Area */}
      <div className="md:container md:mx-auto md:px-4 md:py-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Video Player Section - 70% width on large screens */}
          <div className="w-full lg:w-[70%] relative">
            {/* Video Container */}
            {livePage?.video ? (
              <div className="relative bg-black md:rounded-xl overflow-hidden shadow-xl aspect-video">
                <div className='w-full h-auto fixed top-0 left-0 z-10 md:static'>
                  <video
                  
                    key={slug}
                    ref={videoRef}
                    onLoadedMetadata={handleLoadedMetadata}
                    onTimeUpdate={handleTimeUpdate}
                    onCanPlay={ensurePlay}
                    onEnded={handleVideoEnded}
                    className="w-full h-full"
                    autoPlay
                    muted={isMuted}
                    onPlay={handlePlay}
                  >
                    <source
                      src={
                        livePage.video.startsWith('http')
                          ? livePage.video
                          : `${videoUrl}/${livePage.video}`
                      }
                      type="video/mp4"
                    />
                  </video>

                  {/* Video Overlay Controls */}
                  {isMuted && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={toggleMute}
                      className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-50 text-white cursor-pointer"
                    >
                      <div className="text-center">
                        <IoVolumeMuteSharp className="text-5xl mx-auto mb-2" />
                        <p className="text-lg">Click to unmute</p>
                      </div>
                    </motion.div>
                  )}

                  {/* Live Badge */}
                  <div className="absolute top-[1px] md:top-3 left-2 bg-red-600 text-white px-2 md:px-3 py-[1px] md:py-1 rounded-full text-xs md:text-sm font-bold flex items-center animate-pulse">
                    <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                    LIVE
                  </div>

                  {/* Viewer Count */}
                  <div className="absolute top-[1px] md:top-3 left-16 bg-black bg-opacity-50 text-white px-2 md:px-3 py-[1px] md:py-1 rounded-full text-xs md:text-sm flex items-center">
                    <IoEye className="mr-1" />
                    <span>{viewers}</span>
                  </div>

                  {/* Timer */}
                  <div className="absolute top-[1px] md:top-3 right-2 bg-black bg-opacity-70 text-white px-2 md:px-3 py-[1px] md:py-1 rounded-full text-xs md:text-sm">
                    {formatTime(time)}
                  </div>
                
                </div>


                {/* Special Offer Popup */}
                <AnimatePresence>
                  {showOffer && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    >
                      <motion.div
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        className="bg-white p-6 rounded-xl shadow-2xl max-w-md mx-4 relative"
                      >
                        <button
                          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                          onClick={handleCloseOffer}
                        >
                          <IoClose className="text-2xl" />
                        </button>
                        <div className="flex items-center mb-3">
                          <FaGift className="text-2xl text-yellow-500 mr-2" />
                          <h2 className="text-sm md:text-xl font-bold text-gray-800">
                            {livePage?.offerHeadline}
                          </h2>
                        </div>
                        <p className="text-gray-700 text-sm md:text-md mb-4">{livePage?.offerParagraph}</p>
                        <Link
                          to={livePage?.redirectPage || '#'}
                          onClick={() => {
                            trackEvent('OfferClick', {
                              offer_name: livePage?.offerHeadline,
                              offer_id: slug
                            });
                          }}
                          className="block w-full text-center bg-gradient-to-r text-sm md:text-md from-blue-500 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {livePage?.offerButton || "Get Offer"}
                        </Link>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Bonus Link Popup */}
                <AnimatePresence>
                  {showBonusLinkPopup && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    >
                      <motion.div
                        initial={{ y: 50 }}
                        animate={{ y: 0 }}
                        className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 relative"
                      >
                        <button
                          onClick={() => handleCloseBonusLinkPopup(showBonusLinkPopup.id)}
                          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                        >
                          <IoClose className="text-xl" />
                        </button>
                        
                        <div className="flex items-center mb-4">
                          <span className="text-2xl mr-3">
                            {getBonusLinkIcon(showBonusLinkPopup.type)}
                          </span>
                          <h2 className="text-xl font-bold text-gray-800">
                            {showBonusLinkPopup.title}
                          </h2>
                        </div>
                        
                        {showBonusLinkPopup.description && (
                          <p className="text-gray-600 mb-5">
                            {showBonusLinkPopup.description}
                          </p>
                        )}
                        
                        <a
                          href={showBonusLinkPopup.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleBonusLinkClick(showBonusLinkPopup)}
                          className="block w-full text-center bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all font-medium text-lg"
                        >
                          {showBonusLinkPopup.button_text || "Get Now"}
                        </a>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="bg-gray-200 aspect-video flex items-center justify-center rounded-xl">
                <p className="text-gray-500">Loading video...</p>
              </div>
            )}

            {/* Video Title and Description */}
            {/* <div className="mt-4 bg-white p-4 rounded-xl shadow-sm">
              <h1 className="text-xl font-bold text-gray-800">{livePage?.title}</h1>
              <p className="text-gray-600 mt-1">{livePage?.description}</p>
            </div> */}

            {/* Bonus Links Section (shown below offer after dismissal) */}
            {activeBonusLinks.length > 0 && (
              <div className="mt-4 m-2 space-y-3">
                {activeBonusLinks.map((link, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-400 p-4 rounded-lg"
                  >
                    <div className="flex items-start">
                      <span className="text-xl mr-3 mt-1">
                        {getBonusLinkIcon(link.type)}
                      </span>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800">{link.title}</h3>
                        {link.description && (
                          <p className="text-gray-700 text-sm mt-1">{link.description}</p>
                        )}
                        <a
                          href={link.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleBonusLinkClick(link)}
                          className="inline-block mt-2 bg-blue-500 text-white px-4 py-2 rounded text-sm hover:bg-blue-600 transition-colors font-medium"
                        >
                          {link.button_text || "Get Now"}
                        </a>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            

            {/* Closed Offer Section */}
            {offerClosed && !showOffer && livePage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 m-2 bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-400 p-4 rounded-lg"
              >
                <div className="flex items-start">
                  <FaGift className="text-xl text-yellow-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-800">{livePage.offerHeadline}</h3>
                    <p className="text-gray-700 text-sm mt-1">{livePage.offerParagraph}</p>
                    <Link
                      to={livePage.redirectPage}
                      className="inline-block mt-2 bg-[#fa582d] no-underline font-bold text-center text-white px-3 py-3 text-sm md:text-md rounded hover:bg-[#fa582d] transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {livePage.offerButton}
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Chat Section - 30% width on large screens */}
          <div className="w-full lg:w-[30%] m-2 mb-4">
            <div className="bg-white rounded-xl shadow-sm h-full flex flex-col">
              {/* Chat Header */}
              <div className="border-b border-gray-200 p-4 flex justify-between items-center">
                <h3 className="font-bold text-lg flex items-center">
                  <FaUserCircle className="text-blue-500 mr-2" />
                  Live Chat
                </h3>
                <div className="relative">
                  <button
                    onClick={() => setShowChatOptions(!showChatOptions)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <BsThreeDotsVertical />
                  </button>
                  {showChatOptions && (
                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                      <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Clear Chat
                      </button>
                      <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Report Issue
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Messages Container */}
             <div
                ref={chatRef}
                className="h-72 overflow-y-auto p-4 space-y-3"
                onScroll={(e) =>
                  setIsScrolled(
                    e.target.scrollTop < e.target.scrollHeight - e.target.clientHeight - 10
                  )
                }
              >
                {messages.map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.isYou ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        msg.isYou
                          ? 'bg-blue-500 text-white rounded-br-none'
                          : 'bg-gray-100 text-gray-800 rounded-bl-none'
                      }`}
                    >
                      {!msg.isYou && (
                        <div className="font-bold text-sm mb-1">{msg.name}</div>
                      )}
                      <div className="text-sm">{msg.message}</div>
                    </div>
                  </motion.div>
                ))}
              </div>


              {/* New Message Indicator */}
              {isScrolled && (
                <button
                  onClick={() => {
                    if (chatRef.current) {
                      chatRef.current.scrollTop = chatRef.current.scrollHeight;
                    }
                  }}
                  className="mx-auto mb-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full"
                >
                  New messages ↓
                </button>
              )}

              {/* Message Input */}
              <div className="border-t border-gray-200 p-3">
                <div className="flex items-center">
                  <button className="text-gray-500 hover:text-gray-700 p-2">
                    <FaRegSmile />
                  </button>
                  <input
                    type="text"
                    className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <button
                    onClick={handleSendMessage}
                    className="bg-blue-500 text-white p-2 rounded-r-lg hover:bg-blue-600 transition-colors"
                  >
                    <IoSend />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Live;